import { getAllPublicServerEndpoints } from "../services/helper";
import analyticsConfig from "./analyticsConfig";
import { diagnosticCounters, withinDiagnosticBudget } from "./clientLogs";
import { getPostHog, isPostHogEnabled } from "./posthogClient";
import { createTraceContext, isAlreadyPreflighted, readTraceId, setLastTraceId } from "./trace";

const { EVENTS } = analyticsConfig;

/**
 * Trace correlation for the storefront's API calls.
 *
 * The admin portal has one axios instance to hook; this app calls `fetch`
 * directly from a hundred or so places across services/frontend and the pages,
 * with no chokepoint. Patching `window.fetch` once at boot is the only way to
 * cover them without touching every call site — and it is the same technique
 * utils/clientLogs.js already uses on `console`.
 *
 * Two things happen per request:
 *
 *   1. Outbound, trace headers are attached — but ONLY to our own API, and only
 *      to requests the browser was already going to preflight. See utils/
 *      trace.js for why: on this app that rule is the difference between zero
 *      added round trips and one per catalogue request.
 *   2. Inbound, a server fault or a dropped connection is put on the PostHog
 *      timeline carrying the trace id, so the failure the buyer hit is one
 *      click from the Tempo trace behind it.
 *
 * ── What this must never do ──────────────────────────────────────────────────
 *
 * This wraps every fetch on the storefront, including the ones that place
 * orders. It therefore returns the original promise untouched on every path,
 * rethrows the original error unchanged, and wraps every decision it makes in
 * a try/catch that falls back to calling the original fetch with the original
 * arguments. A bug in here must be able to cost a correlation and nothing else.
 */

let installed = false;
let apiOrigins = null;

const originOf = (url) => {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
};

/**
 * The origins worth tracing: our own API hosts, plus this page's own origin
 * (the Next API routes under /api/, which being same-origin are never
 * preflighted and so can always carry the headers for free).
 *
 * Anything else — S3, CloudFront, Razorpay, a font CDN — is left strictly
 * alone. Sending a custom header to a third party is how you turn a working
 * image request into a CORS failure.
 */
const getApiOrigins = () => {
  if (apiOrigins) return apiOrigins;

  const configured = [...getAllPublicServerEndpoints(), window.location.origin];

  apiOrigins = new Set(configured.filter(Boolean).map(originOf).filter(Boolean));
  return apiOrigins;
};

/** The path with ids stripped, so one endpoint is one row rather than one per product. */
const routeOf = (url) => {
  try {
    const { pathname } = new URL(url, window.location.href);
    return pathname
      .split("/")
      .map((segment) => {
        if (/^[0-9a-f]{24}$/i.test(segment)) return ":id";
        if (/^[0-9a-f-]{36}$/i.test(segment)) return ":uuid";
        if (/^\d+$/.test(segment)) return ":n";
        return segment;
      })
      .join("/");
  } catch {
    return undefined;
  }
};

/**
 * Put a failed API call on the buyer's timeline.
 *
 * Only server faults and dropped connections. A 4xx is the API answering
 * correctly — out of stock, below minimum order, bad OTP — and this app already
 * has purpose-built events for the ones that matter (`order_failed`,
 * `MinOrderAlert`). Recording the rest would spend the free tier on normal
 * operation.
 *
 * `status === undefined` means the request never completed: offline, DNS,
 * timeout, CORS. That case is always worth recording precisely because no
 * server-side trace exists for it — nothing arrived.
 */
export const reportApiFailure = ({ traceId, status, method, url, message, route: knownRoute }) => {
  if (!isPostHogEnabled()) return;

  // The tunnel passes an opaque `t:<code>` here; see services/api/tunnelFetch.js.
  const route = knownRoute || routeOf(url);
  const diagnosticKey = `api_request_failed:${status || "network"}:${route || "?"}`;
  withinDiagnosticBudget(diagnosticKey);

  try {
    getPostHog()?.capture(EVENTS.API_REQUEST_FAILED, {
      trace_id: traceId,
      http_status: status === undefined ? null : status,
      http_method: String(method || "GET").toUpperCase(),
      api_route: route,
      failure_message: message ? String(message).slice(0, 200) : undefined,
      log_path: window.location.pathname,
      ...diagnosticCounters(diagnosticKey),
    });
  } catch {
    // Reporting a failure must never raise one.
  }
};

/**
 * Merge trace headers into whatever shape this call site passed, without
 * disturbing the headers it set itself.
 *
 * Returns the original arguments unchanged when the request is not ours to
 * trace, or when it is a simple cross-origin request that would be made
 * non-simple — which is the whole point.
 */
const withTraceHeaders = (input, init) => {
  const isRequest = typeof Request !== "undefined" && input instanceof Request;
  const url = isRequest ? input.url : String(input);

  if (!getApiOrigins().has(originOf(url))) return { input, init, traceId: undefined };

  const method = (init && init.method) || (isRequest ? input.method : "GET");
  const headers = (init && init.headers) || (isRequest ? input.headers : undefined);

  // Same-origin requests never preflight, so the rule only applies across
  // origins. This is what lets the Next API routes under /api/ always carry it.
  const crossOrigin = originOf(url) !== window.location.origin;
  if (crossOrigin && !isAlreadyPreflighted(method, headers)) {
    return { input, init, traceId: undefined };
  }

  const existing = new Headers((isRequest && !init ? input.headers : headers) || undefined);

  // Something else already traced this request — leave its context alone.
  //
  // This is not hypothetical here. The New Relic browser agent is still loaded
  // in this app (pages/_app.js) with `distributed_tracing.enabled: true`, and
  // it injects its own W3C `traceparent` on same-origin AJAX. Both agents patch
  // fetch, so which one wraps last is a race; overwriting a traceparent that is
  // already present would break whichever tracer set it, for no gain. Ours is
  // additive or it does not go.
  if (existing.has("traceparent")) {
    return { input, init, traceId: undefined };
  }

  const { traceId, headers: traceHeaders } = createTraceContext();
  // Becomes the correlation point for any exception that follows this request
  // but carries no trace context of its own — see getLastTraceId in trace.js.
  setLastTraceId(traceId);
  const merged = existing;
  Object.entries(traceHeaders).forEach(([name, value]) => merged.set(name, value));

  if (isRequest && !init) {
    // A Request's headers are immutable; clone it with the extra ones. The
    // clone copies method, body, credentials, mode and signal.
    return { input: new Request(input, { headers: merged }), init, traceId };
  }

  return { input, init: { ...(init || {}), headers: merged }, traceId };
};

/**
 * Patch window.fetch. Idempotent, client-only, and safe to call before PostHog
 * has loaded.
 */
export const installApiTracing = () => {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input, init) => {
    let request = { input, init, traceId: undefined };

    try {
      request = withTraceHeaders(input, init);
    } catch {
      // Fall through with the untouched arguments: a request that goes out
      // uncorrelated is infinitely better than one that does not go out.
      request = { input, init, traceId: undefined };
    }

    const promise = originalFetch(request.input, request.init);
    const url = typeof request.input === "string" ? request.input : request.input?.url;
    const method = (request.init && request.init.method) || request.input?.method || "GET";
    // The API tunnel's own requests. tunnelFetch reports those itself, with the logical status
    // and an opaque route code; reported here they would all read `POST /t`, and a server
    // fault inside the tunnel arrives as a 200 that this could not see anyway.
    let isTunnel = false;
    try {
      isTunnel = getApiOrigins().has(originOf(url)) && routeOf(url) === "/t";
    } catch {
      // Unknown means ordinary: report as before.
    }

    return promise.then(
      (response) => {
        try {
          // Prefer the backend's own view; fall back to what we sent, which is
          // all there is when the request carried no traceparent. Recorded for
          // every response, not just failures: a later render crash correlates
          // to the last request the page made, whether or not it succeeded.
          const responseTraceId = readTraceId(response) || request.traceId;
          setLastTraceId(responseTraceId);

          // Web reports 5xx and dropped connections only — a 4xx is the API
          // answering correctly (out of stock, below minimum order, bad OTP),
          // and the ones worth knowing about already have their own events
          // (order_failed, MinOrderAlert). Mobile is the one platform that
          // also records 4xx — see POSTHOG.md.
          if (!isTunnel && response && response.status >= 500) {
            reportApiFailure({
              traceId: responseTraceId,
              status: response.status,
              method,
              url,
            });
          }
        } catch {
          // Never let reporting change what the caller receives.
        }
        return response;
      },
      (error) => {
        try {
          if (!isTunnel) {
            reportApiFailure({
              traceId: request.traceId,
              status: undefined,
              method,
              url,
              message: error && error.message,
            });
          }
        } catch {
          // As above.
        }
        // The original rejection, unchanged: call sites branch on it.
        throw error;
      },
    );
  };
};
